import React, { useEffect, useState } from 'react'
import { API } from 'aws-amplify'
import Navbar from '../components/navbar'
import Footer from '../components/footer'

interface Event {
  id: string
  created_at: number
  title: string
  message: string
  image_url?: string
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await API.get('MasjidNotifierAPI', '/events', {})
      setEvents(response)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events')
    } finally {
      setIsLoading(false)
    }
  }

  const deleteEvent = async (event: Event): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
      return
    }

    setDeletingId(event.id)
    try {
      await API.del('MasjidNotifierAPI', '/events', {
        queryStringParameters: {
          id: event.id,
          created_at: event.created_at.toString()
        }
      })
      setEvents(events.filter(e => e.id !== event.id))
    } catch (err) {
      console.error('Error deleting event:', err)
      setError('Failed to delete event')
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 flex py-8 px-4">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="text-gray-600 font-bold">Manage News Feed</div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-teal-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
              {error}
              <button onClick={fetchEvents} className="ml-2 underline">Retry</button>
            </div>
          )}

          {!isLoading && events.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No events found
            </div>
          )}

          {!isLoading && events.length > 0 && (
            <div className="space-y-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{event.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatDate(event.created_at)}</p>
                    </div>

                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt=""
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                    )}

                    <button
                      onClick={() => deleteEvent(event)}
                      disabled={deletingId === event.id}
                      className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title="Delete event"
                    >
                      {deletingId === event.id ? (
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {events.length} event{events.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={fetchEvents}
              disabled={isLoading}
              className="px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default EventsPage
